import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  User,
  Search,
  Mail,
  CheckCircle,
  Clock,
  Loader2,
  ShieldCheck,
  Plus,
  Send,
  Users,
  Ban,
  XCircle,
  MoreHorizontal,
  Eye,
  Archive,
  ArchiveRestore,
  RotateCcw,
  Pencil,
  ChevronUp,
  ChevronDown,
  ChevronDownIcon,
  X,
  Calendar,
} from 'lucide-react';

import SmartPagination from '@/components/ui/smart-pagination';
import { practitionerService } from '@/services/practitionerService';
import { PaginationParams, Practitioner, PractitionerInvitation } from '@/lib/types';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import ArchivePractitionerDialog from '@/components/dialog/ArchivePractitionerDialog';
import AddPractitionerSheet from '@/components/clinic-detail/AddPractitionerSheet';
import EditPractitionerSheet from '@/components/clinic-detail/EditPractitionerSheet';
import ResubmitPractitionerSheet from '@/components/clinic-detail/ResubmitPractitionerSheet';
import InvitePractitionerDialog from '@/components/dialog/InvitePractitionerDialog';
import { permissionCheck } from '@/lib/permission-check';
import { useClinic } from '@/contexts/ClinicContext';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import StatusBadgeWithTooltip from '@/components/StatusBadgeWithTooltip';

const PractitionersPage = () => {
  const { user } = useAuth();
  const { clinic, isPractitioner, isAdmin, rolePermissions, isClinicStaff, isGuided } =
    useClinic();
  const [searchTerm, setSearchTerm] = useState('');
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [invitations, setInvitations] = useState<PractitionerInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [addPractitionerSheetOpen, setAddPractitionerSheetOpen] = useState(false);
  const [invitePractitionerDialogOpen, setInvitePractitionerDialogOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'practitioners' | 'invitations'>('practitioners');
  const [invitationStatusFilter, setInvitationStatusFilter] = useState('');
  const [practitionerStatusFilter, setPractitionerStatusFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    itemsPerPage: 10,
    totalItems: 0,
  });
  const [invitationPagination, setInvitationPagination] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    itemsPerPage: 10,
    totalItems: 0,
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  // Dialog states
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [resubmitSheetOpen, setResubmitSheetOpen] = useState(false);
  const [editPractitionerSheetOpen, setEditPractitionerSheetOpen] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | null>(null);
  const [isResending, setIsResending] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState<string | null>(null);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp className='h-3 w-3' />
    ) : (
      <ChevronDown className='h-3 w-3' />
    );
  };

  const handlePageChange = (page: number) => {
    setPagination({
      ...pagination,
      page: page,
    });
  };

  const fetchPractitioners = async (statusFilter?: string) => {
    try {
      setLoading(true);
      const response = await practitionerService.getPractitionerByClinicId(clinic?.id!, {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        sortBy: sortField,
        sortOrder: sortOrder,
        status: statusFilter ?? practitionerStatusFilter,
      });
      setPractitioners(response.practitioners as Practitioner[]);
      setPagination(prev => ({
        ...prev,
        ...response.pagination,
      }));
    } catch (error) {
      console.error('Error fetching practitioners:', error);
      toast({
        title: 'Error fetching practitioners',
        description: 'Please try again later',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await practitionerService.getInvitations(clinic?.id!, {
        page: invitationPagination.page,
        limit: invitationPagination.limit,
        search: searchTerm,
        status: invitationStatusFilter,
      });
      setInvitations(response.invitations as PractitionerInvitation[]);
      setInvitationPagination(prev => ({
        ...prev,
        ...response.pagination,
      }));
    } catch (error) {
      console.error('Error fetching invitations:', error);
      toast({
        title: 'Error fetching invitations',
        description: 'Please try again later',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      setIsResending(invitationId);
      await practitionerService.resendInvitation(clinic?.id!, invitationId);
      toast({
        title: 'Invitation resent',
        description: 'The invitation has been resent successfully',
        variant: 'success',
      });
      fetchInvitations();
    } catch (error: any) {
      toast({
        title: 'Error resending invitation',
        description: error?.response?.data?.error?.message || 'Please try again later',
        variant: 'destructive',
      });
    } finally {
      setIsResending(null);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      setIsCancelling(invitationId);
      await practitionerService.cancelInvitation(clinic?.id!, invitationId);
      toast({
        title: 'Invitation cancelled',
        description: 'The invitation has been cancelled successfully',
        variant: 'success',
      });
      fetchInvitations();
    } catch (error: any) {
      toast({
        title: 'Error cancelling invitation',
        description: error?.response?.data?.error?.message || 'Please try again later',
        variant: 'destructive',
      });
    } finally {
      setIsCancelling(null);
    }
  };

  useEffect(() => {
    if (currentView === 'practitioners') {
      fetchPractitioners(practitionerStatusFilter);
    } else {
      fetchInvitations();
    }
  }, [
    currentView,
    pagination.page,
    pagination.limit,
    invitationPagination.page,
    invitationPagination.limit,
    searchTerm,
    sortField,
    sortOrder,
    practitionerStatusFilter,
    invitationStatusFilter,
  ]);


  const getInvitationStatusColor = (status: string, is_active: boolean = true) => {
    if (!is_active) return 'bg-slate-500 hover:bg-slate-500 text-white border-0';
    switch (status) {
      case 'pending':
        return 'bg-amber-500 hover:bg-amber-500 text-white border-0';
      case 'accepted':
        return 'bg-emerald-600 hover:bg-emerald-600 text-white border-0';
      case 'expired':
        return 'bg-red-600 hover:bg-red-600 text-white border-0';
      case 'cancelled':
        return 'bg-slate-500 hover:bg-slate-500 text-white border-0';
      default:
        return 'bg-slate-100 hover:bg-slate-100 text-slate-800 border-0';
    }
  };

  const getInvitationStatusIcon = (status: string, is_active: boolean = true) => {
    if (!is_active) return <Ban className='h-3 w-3' />;
    switch (status) {
      case 'pending':
        return <Clock className='h-3 w-3' />;
      case 'accepted':
        return <CheckCircle className='h-3 w-3' />;
      case 'expired':
        return <XCircle className='h-3 w-3' />;
      case 'cancelled':
        return <X className='h-3 w-3' />;
      default:
        return null;
    }
  };

  const handleInvitationPageChange = (page: number) => {
    setInvitationPagination({
      ...invitationPagination,
      page: page,
    });
  };

  const getStatusFilterLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'requested':
        return 'Pending Approval';
      case 'pending':
        return 'Pending';
      case 'pending_acceptance':
        return 'Pending Acceptance';
      case 'rejected':
        return 'Rejected';
      case 'deactivated':
        return 'Deactivated';
      case 'archive':
        return 'Archived';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const handleNavigate = (practitioner: Practitioner) => {
    navigate(`/practitioners/${practitioner.id}`);
  };

  const handleArchive = (practitioner: Practitioner) => {
    setSelectedPractitioner(practitioner);
    setArchiveDialogOpen(true);
  };

  const handleResubmit = (practitioner: Practitioner) => {
    setSelectedPractitioner(practitioner);
    setResubmitSheetOpen(true);
  };

  // Rotate colors for practitioner icons
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-600' },
    { bg: 'bg-purple-100', text: 'text-purple-600' },
    { bg: 'bg-pink-100', text: 'text-pink-600' },
    { bg: 'bg-orange-100', text: 'text-orange-600' },
    { bg: 'bg-amber-100', text: 'text-amber-600' },
    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    { bg: 'bg-rose-100', text: 'text-rose-600' },
    { bg: 'bg-violet-100', text: 'text-violet-600' },
    { bg: 'bg-teal-100', text: 'text-teal-600' },
  ];

  return (
    <div className='space-y-4 sm:space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>
            Practitioners
          </h1>
          <p className='mt-1 text-sm text-slate-500'>
            Manage your clinic's healthcare practitioners
          </p>
        </div>
        {!isGuided &&
          permissionCheck(
            'practitioners:create',
            rolePermissions,
            isPractitioner || isAdmin
          ) && (
            <div className='flex w-full items-center gap-3 sm:w-auto'>
              <Button
                variant='outline'
                onClick={() => setInvitePractitionerDialogOpen(true)}
                className='h-10 flex-1 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-700 sm:flex-none'
              >
                <Send className='mr-2 h-4 w-4' />
                Invite
              </Button>
              <Button
                onClick={() => setAddPractitionerSheetOpen(true)}
                className='h-10 flex-1 gap-2 rounded-lg bg-brand-teal px-4 text-white hover:bg-brand-teal/90 sm:flex-none'
              >
                <Plus className='h-4 w-4' />
                Add Practitioner
              </Button>
            </div>
          )}
      </div>

      {/* Tabs */}
      <Tabs
        value={currentView}
        onValueChange={(value) => {
          setCurrentView(value as 'practitioners' | 'invitations');
          setSearchTerm('');
        }}
        className='w-full'
      >
        <TabsList
          className='w-full sm:w-auto justify-start gap-1 p-1 h-auto rounded-lg border border-slate-200'
          style={{ backgroundColor: 'rgb(245, 247, 247)' }}
        >
          <TabsTrigger
            value='practitioners'
            className='flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600'
          >
            <Users className='h-4 w-4' />
            Practitioners
          </TabsTrigger>
          <TabsTrigger
            value='invitations'
            className='flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600'
          >
            <Mail className='h-4 w-4' />
            Invitations
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search Row */}
      <div className='flex flex-col items-stretch gap-3 sm:flex-row sm:items-center'>
        <div className='relative w-full sm:w-auto sm:min-w-[200px] md:min-w-[280px] lg:min-w-[380px]'>
          <Search className='absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400' />
          <Input
            placeholder={currentView === 'practitioners' ? 'Search by name' : 'Search by email'}
            className='w-full rounded-lg border-slate-200 bg-white pl-9'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value.trimStart())}
          />
        </div>

        {/* Filters Row - Half on mobile, normal on desktop */}
        <div className='flex flex-1 items-center gap-3'>

          {/* Spacer - only visible on desktop */}
          <div className='hidden sm:flex sm:flex-1' />

          {/* Practitioner Status Filter */}
          {currentView === 'practitioners' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='outline'
                  className='h-10 flex-1 justify-between whitespace-nowrap rounded-lg border-slate-200 bg-white px-3 focus-visible:ring-0 focus-visible:ring-offset-0 sm:flex-none sm:justify-start sm:px-4'
                >
                  <span>
                    {practitionerStatusFilter
                      ? getStatusFilterLabel(practitionerStatusFilter)
                      : 'All Statuses'}
                  </span>
                  <ChevronDownIcon className='ml-2 h-4 w-4 text-slate-400' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-48'>
                <DropdownMenuItem
                  onClick={() => {
                    setPractitionerStatusFilter('');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className='cursor-pointer'
                >
                  All Statuses
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setPractitionerStatusFilter('active');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className='cursor-pointer'
                >
                  <CheckCircle className='mr-2 h-4 w-4 text-emerald-600' />
                  Active
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setPractitionerStatusFilter('pending_acceptance');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className='cursor-pointer'
                >
                  <Mail className='mr-2 h-4 w-4 text-amber-500' />
                  Pending Acceptance
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setPractitionerStatusFilter('requested');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className='cursor-pointer'
                >
                  <Clock className='mr-2 h-4 w-4 text-sky-500' />
                  Pending Approval
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setPractitionerStatusFilter('pending');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className='cursor-pointer'
                >
                  <Loader2 className='mr-2 h-4 w-4 text-amber-500' />
                  Pending
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setPractitionerStatusFilter('rejected');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className='cursor-pointer'
                >
                  <XCircle className='mr-2 h-4 w-4 text-red-600' />
                  Rejected
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setPractitionerStatusFilter('archive');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className='cursor-pointer'
                >
                  <Ban className='mr-2 h-4 w-4 text-slate-500' />
                  Archived
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setPractitionerStatusFilter('deactivated');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className='cursor-pointer'
                >
                  <Ban className='mr-2 h-4 w-4 text-slate-500' />
                  Deactivated
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Invitation Status Filter */}
          {currentView === 'invitations' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='outline'
                  className='h-10 flex-1 justify-between whitespace-nowrap rounded-lg border-slate-200 bg-white px-3 focus-visible:ring-0 focus-visible:ring-offset-0 sm:flex-none sm:justify-start sm:px-4'
                >
                  <span>
                    {invitationStatusFilter
                      ? invitationStatusFilter.charAt(0).toUpperCase() +
                        invitationStatusFilter.slice(1)
                      : 'All Statuses'}
                  </span>
                  <ChevronDownIcon className='ml-2 h-4 w-4 text-slate-400' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-40'>
                <DropdownMenuItem
                  onClick={() => setInvitationStatusFilter('')}
                  className='cursor-pointer'
                >
                  All Statuses
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setInvitationStatusFilter('pending')}
                  className='cursor-pointer'
                >
                  Pending
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setInvitationStatusFilter('accepted')}
                  className='cursor-pointer'
                >
                  Accepted
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setInvitationStatusFilter('expired')}
                  className='cursor-pointer'
                >
                  Expired
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setInvitationStatusFilter('cancelled')}
                  className='cursor-pointer'
                >
                  Cancelled
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Active Filters Pills */}
      {((currentView === 'practitioners' && practitionerStatusFilter) ||
        (currentView === 'invitations' && invitationStatusFilter)) && (
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-xs text-slate-500'>Filters:</span>
          {currentView === 'practitioners' && practitionerStatusFilter && (
            <Badge
              variant='secondary'
              className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
            >
              Status: {getStatusFilterLabel(practitionerStatusFilter)}
              <button
                onClick={() => {
                  setPractitionerStatusFilter('');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className='ml-1.5 hover:text-slate-900'
              >
                <X className='h-3 w-3' />
              </button>
            </Badge>
          )}
          {currentView === 'invitations' && invitationStatusFilter && (
            <Badge
              variant='secondary'
              className='bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100'
            >
              Status:{' '}
              {invitationStatusFilter.charAt(0).toUpperCase() + invitationStatusFilter.slice(1)}
              <button
                onClick={() => setInvitationStatusFilter('')}
                className='ml-1.5 hover:text-slate-900'
              >
                <X className='h-3 w-3' />
              </button>
            </Badge>
          )}
          <button
            onClick={() => {
              if (currentView === 'practitioners') {
                setPractitionerStatusFilter('');
                setPagination(prev => ({ ...prev, page: 1 }));
              } else {
                setInvitationStatusFilter('');
              }
            }}
            className='text-xs text-brand-teal hover:underline'
          >
            Clear all
          </button>
        </div>
      )}

      {/* Practitioners Table */}
      {currentView === 'practitioners' && (
        <div className='overflow-hidden rounded-md border border-slate-200 bg-white'>
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[900px] text-sm'>
              <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                <tr className='border-b border-slate-200'>
                  <th
                    className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    #
                  </th>
                  <th
                    className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                    style={{ fontSize: '0.7rem' }}
                    onClick={() => handleSort('full_name')}
                  >
                    <div className='flex items-center gap-1'>
                      Name
                      <SortIcon field='full_name' />
                    </div>
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    DEA
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    NPI
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Email
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Phone
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    License
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Patients
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Status
                  </th>
                  <th
                    className='sticky right-0 w-12 px-2 py-3'
                    style={{
                      background:
                        'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)',
                    }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index} className='border-b border-slate-100'>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-10' />
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-3'>
                          <Skeleton className='h-8 w-8 rounded-lg' />
                          <Skeleton className='h-4 w-28' />
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-20' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-36' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-24' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-20' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-8' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-5 w-16 rounded-full' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-8 w-8 rounded' />
                      </td>
                    </tr>
                  ))
                ) : practitioners.length === 0 ? (
                  <tr>
                    <td colSpan={9} className='h-48 text-center'>
                      <div className='flex flex-col items-center justify-center text-slate-500'>
                        <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100'>
                          <Users className='h-7 w-7 text-slate-400' />
                        </div>
                        <p className='font-medium text-slate-600'>
                          {searchTerm ? 'No practitioners found' : 'No practitioners yet'}
                        </p>
                        <p className='mt-1 text-sm text-slate-400'>
                          {searchTerm
                            ? 'Try adjusting your search'
                            : 'Add your first practitioner to get started'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  practitioners.map((practitioner, index) => {
                    const color = colors[index % colors.length];
                    const isSelf =
                      practitioner.user?.id && practitioner.user?.id === user?.id;
                    return (
                      <tr
                        key={practitioner.id}
                        className='cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/50'
                        onClick={() => handleNavigate(practitioner)}
                      >
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-500'
                          style={{ fontSize: '0.8rem' }}
                        >
                          #
                          {String((pagination.page - 1) * pagination.limit + index + 1).padStart(
                            3,
                            '0'
                          )}
                        </td>
                        <td
                          className='max-w-[200px] whitespace-nowrap px-4 py-3'
                          style={{ fontSize: '0.8rem' }}
                        >
                          <div className='flex items-center gap-3'>
                            <div
                              className={`h-8 w-8 ${color.bg} flex flex-shrink-0 items-center justify-center rounded-lg`}
                            >
                              <User className={`h-4 w-4 ${color.text}`} />
                            </div>
                            <div className='min-w-0'>
                              <span className='block max-w-[150px] truncate font-medium capitalize text-slate-900'>
                                {practitioner.full_name}
                              </span>
                              <div className='mt-0.5 flex flex-wrap gap-1'>
                                {practitioner.user?.role === 'admin' && (
                                  <Badge className='border-0 bg-brand-copper px-1.5 py-0 text-[10px] text-white hover:bg-brand-copper whitespace-nowrap'>
                                    <ShieldCheck className='mr-1 h-2.5 w-2.5' />
                                    Clinic Admin
                                  </Badge>
                                )}
                                <Badge className='border-0 bg-slate-100 px-1.5 py-0 text-[10px] text-slate-700 hover:bg-slate-100 whitespace-nowrap'>
                                  Practitioner
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-600'
                          style={{ fontSize: '0.8rem' }}
                        >
                          {practitioner.dea || '--'}
                        </td>
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-600'
                          style={{ fontSize: '0.8rem' }}
                        >
                          {practitioner.npi || '--'}
                        </td>
                        <td
                          className='max-w-[150px] px-4 py-3 text-slate-600'
                          style={{ fontSize: '0.8rem' }}
                        >
                          <span className='block truncate' title={practitioner.email || '--'}>
                            {practitioner.email || '--'}
                          </span>
                        </td>
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-600'
                          style={{ fontSize: '0.8rem' }}
                        >
                          {practitioner.phone || '--'}
                        </td>
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-600'
                          style={{ fontSize: '0.8rem' }}
                        >
                          {practitioner.license_number || '--'}
                        </td>
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-600'
                          style={{ fontSize: '0.8rem' }}
                        >
                          {practitioner.patient_count ?? 0}
                        </td>
                        <td className='whitespace-nowrap px-4 py-3'>
                          <StatusBadgeWithTooltip
                            status={practitioner.status}
                            userStatus={practitioner.user?.status}
                            isActive={practitioner.is_active}
                          />
                        </td>
                        <td
                          className='sticky right-0 w-12 px-2 py-3'
                          style={{
                            background:
                              'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
                          }}
                          onClick={e => e.stopPropagation()}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant='ghost'
                                size='icon'
                                className='h-8 w-8 hover:bg-slate-100'
                              >
                                <MoreHorizontal className='h-4 w-4 text-slate-500' />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end' className='w-40'>
                              <DropdownMenuItem
                                onClick={() => handleNavigate(practitioner)}
                                className='cursor-pointer'
                                style={{ fontSize: '13px' }}
                              >
                                <Eye className='mr-2 h-4 w-4 text-slate-500' />
                                View Details
                              </DropdownMenuItem>
                              {(practitioner.status === 'active' ||
                                practitioner.status === 'archive') &&
                                permissionCheck(
                                  'practitioners:update',
                                  rolePermissions,
                                  isAdmin
                                ) && (
                                  <DropdownMenuItem
                                    onClick={e => {
                                      e.stopPropagation();
                                      setSelectedPractitioner(practitioner);
                                      setEditPractitionerSheetOpen(true);
                                    }}
                                    className='cursor-pointer'
                                    style={{ fontSize: '13px' }}
                                  >
                                    <Pencil className='mr-2 h-4 w-4 text-slate-500' />
                                    Edit
                                  </DropdownMenuItem>
                                )}
                              {practitioner.status === 'rejected' && isAdmin && (
                                <DropdownMenuItem
                                  onClick={() => handleResubmit(practitioner)}
                                  className='cursor-pointer'
                                  style={{ fontSize: '13px' }}
                                >
                                  <RotateCcw className='mr-2 h-4 w-4 text-slate-500' />
                                  Resubmit
                                </DropdownMenuItem>
                              )}
                              {practitioner.is_active &&
                              practitioner.status === 'active' &&
                              !isSelf
                                ? permissionCheck(
                                    'practitioners:update',
                                    rolePermissions,
                                    isPractitioner || isAdmin
                                  ) && (
                                    <DropdownMenuItem
                                      onClick={() => handleArchive(practitioner)}
                                      className='cursor-pointer text-red-600'
                                      style={{ fontSize: '13px' }}
                                    >
                                      <Archive className='mr-2 h-4 w-4' />
                                      Archive
                                    </DropdownMenuItem>
                                  )
                                : practitioner.status === 'active' &&
                                  !isSelf &&
                                  permissionCheck(
                                    'practitioners:update',
                                    rolePermissions,
                                    isPractitioner || isAdmin
                                  ) && (
                                    <DropdownMenuItem
                                      onClick={() => handleArchive(practitioner)}
                                      className='cursor-pointer text-emerald-600'
                                      style={{ fontSize: '13px' }}
                                    >
                                      <ArchiveRestore className='mr-2 h-4 w-4' />
                                      Restore
                                    </DropdownMenuItem>
                                  )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className='flex flex-col items-center gap-3 border-t border-slate-200 px-2 py-3 sm:flex-row sm:justify-between sm:px-4'>
              <SmartPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                className='mx-0 justify-center sm:justify-start'
                maxVisiblePages={3}
              />
              <p className='whitespace-nowrap text-xs text-slate-500'>
                Total: <span className='font-medium text-slate-700'>{pagination.totalItems}</span>{' '}
                Practitioners
              </p>
            </div>
          )}
        </div>
      )}

      {/* Invitations Table */}
      {currentView === 'invitations' && (
        <div className='overflow-hidden rounded-md border border-slate-200 bg-white'>
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[800px] text-sm'>
              <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                <tr className='border-b border-slate-200'>
                  <th
                    className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    #
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Email
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Invited By
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Sent Date
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Expires
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Status
                  </th>
                  <th
                    className='sticky right-0 w-12 px-2 py-3'
                    style={{
                      background:
                        'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)',
                    }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index} className='border-b border-slate-100'>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-10' />
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-3'>
                          <Skeleton className='h-8 w-8 rounded-lg' />
                          <Skeleton className='h-4 w-40' />
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-28' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-24' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-4 w-24' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-5 w-16 rounded-full' />
                      </td>
                      <td className='px-4 py-3'>
                        <Skeleton className='h-8 w-8 rounded' />
                      </td>
                    </tr>
                  ))
                ) : invitations.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='h-48 text-center'>
                      <div className='flex flex-col items-center justify-center text-slate-500'>
                        <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100'>
                          <Mail className='h-7 w-7 text-slate-400' />
                        </div>
                        <p className='font-medium text-slate-600'>
                          {searchTerm ? 'No invitations found' : 'No invitations yet'}
                        </p>
                        <p className='mt-1 text-sm text-slate-400'>
                          {searchTerm
                            ? 'Try adjusting your search'
                            : 'Invite practitioners to join your clinic'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  invitations.map((invitation, index) => {
                    const color = colors[index % colors.length];
                    const isExpired = new Date(invitation.expires_at) < new Date();
                    return (
                      <tr
                        key={invitation.id}
                        className='border-b border-slate-100 transition-colors hover:bg-slate-50/50'
                      >
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-500'
                          style={{ fontSize: '0.8rem' }}
                        >
                          #
                          {String(
                            (invitationPagination.page - 1) * invitationPagination.limit + index + 1
                          ).padStart(3, '0')}
                        </td>
                        <td
                          className='max-w-[250px] whitespace-nowrap px-4 py-3'
                          style={{ fontSize: '0.8rem' }}
                        >
                          <div className='flex items-center gap-3'>
                            <div
                              className={`h-8 w-8 ${color.bg} flex flex-shrink-0 items-center justify-center rounded-lg`}
                            >
                              <Mail className={`h-4 w-4 ${color.text}`} />
                            </div>
                            <span className='block truncate font-medium text-slate-900'>
                              {invitation.email}
                            </span>
                          </div>
                        </td>
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-600'
                          style={{ fontSize: '0.8rem' }}
                        >
                          {invitation.inviter
                            ? `${invitation.inviter.first_name} ${invitation.inviter.last_name}`
                            : '--'}
                        </td>
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-600'
                          style={{ fontSize: '0.8rem' }}
                        >
                          <div className='flex items-center gap-1'>
                            <Calendar className='h-3.5 w-3.5 text-slate-400' />
                            {format(new Date(invitation.created_at), 'MMM dd, yyyy')}
                          </div>
                        </td>
                        <td
                          className='whitespace-nowrap px-4 py-3 text-slate-600'
                          style={{ fontSize: '0.8rem' }}
                        >
                          <div className='flex items-center gap-1'>
                            <Clock className='h-3.5 w-3.5 text-slate-400' />
                            {format(new Date(invitation.expires_at), 'MMM dd, yyyy')}
                          </div>
                        </td>
                        <td className='whitespace-nowrap px-4 py-3'>
                          <div className='flex items-center gap-2'>
                            <Badge
                              className={`${getInvitationStatusColor(invitation.status)} inline-flex w-fit items-center gap-1 text-xs`}
                            >
                              {getInvitationStatusIcon(invitation.status)}
                              <span className='capitalize'>{invitation.status}</span>
                            </Badge>
                            {isExpired && invitation.status === 'pending' && (
                              <Badge className='border-0 bg-red-600 text-xs text-white hover:bg-red-600'>
                                <XCircle className='mr-1 h-3 w-3' />
                                Expired
                              </Badge>
                            )}
                          </div>
                        </td>
                        {/* Only show actions column if there are actions available */}
                        {(invitation.status === 'pending' || invitation.status === 'expired') &&
                        permissionCheck(
                          'practitioners:create',
                          rolePermissions,
                          isAdmin ||
                            permissionCheck('practitioners:update', rolePermissions, isAdmin)
                        ) ? (
                          <td
                            className='sticky right-0 w-12 px-2 py-3'
                            style={{
                              background:
                                'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
                            }}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8 hover:bg-slate-100'
                                >
                                  <MoreHorizontal className='h-4 w-4 text-slate-500' />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='end' className='w-36'>
                                {(invitation.status === 'pending' ||
                                  invitation.status === 'expired') && (
                                  <DropdownMenuItem
                                    onClick={() => handleResendInvitation(invitation.id)}
                                    className='cursor-pointer'
                                    style={{ fontSize: '13px' }}
                                    disabled={isResending === invitation.id}
                                  >
                                    {isResending === invitation.id ? (
                                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                    ) : (
                                      <Send className='mr-2 h-4 w-4 text-slate-500' />
                                    )}
                                    {isResending === invitation.id ? 'Resending...' : 'Resend'}
                                  </DropdownMenuItem>
                                )}
                                {invitation.status === 'pending' && (
                                  <DropdownMenuItem
                                    onClick={() => handleCancelInvitation(invitation.id)}
                                    className='cursor-pointer text-red-600'
                                    style={{ fontSize: '13px' }}
                                    disabled={isCancelling === invitation.id}
                                  >
                                    {isCancelling === invitation.id ? (
                                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                    ) : (
                                      <X className='mr-2 h-4 w-4' />
                                    )}
                                    {isCancelling === invitation.id ? 'Cancelling...' : 'Cancel'}
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        ) : (
                          <td
                            className='sticky right-0 w-12 px-2 py-3'
                            style={{
                              background:
                                'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
                            }}
                          ></td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {invitationPagination.totalPages > 1 && (
            <div className='flex flex-col items-center gap-3 border-t border-slate-200 px-2 py-3 sm:flex-row sm:justify-between sm:px-4'>
              <SmartPagination
                currentPage={invitationPagination.page}
                totalPages={invitationPagination.totalPages}
                onPageChange={handleInvitationPageChange}
                className='mx-0 justify-center sm:justify-start'
                maxVisiblePages={3}
              />
              <p className='whitespace-nowrap text-xs text-slate-500'>
                Total:{' '}
                <span className='font-medium text-slate-700'>
                  {invitationPagination.totalItems}
                </span>{' '}
                Invitations
              </p>
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      <AddPractitionerSheet
        open={addPractitionerSheetOpen}
        onOpenChange={setAddPractitionerSheetOpen}
        onSuccess={() => {
          fetchPractitioners();
        }}
        clinicId={clinic?.id!}
      />
      <InvitePractitionerDialog
        open={invitePractitionerDialogOpen}
        onOpenChange={setInvitePractitionerDialogOpen}
        clinicId={clinic?.id!}
        onSuccess={() => {
          fetchPractitioners();
        }}
      />
      {archiveDialogOpen && selectedPractitioner && (
        <ArchivePractitionerDialog
          open={archiveDialogOpen}
          onOpenChange={setArchiveDialogOpen}
          practitioner={selectedPractitioner}
          onSuccess={() => {
            setArchiveDialogOpen(false);
            setSelectedPractitioner(null);
            fetchPractitioners();
          }}
        />
      )}
      {resubmitSheetOpen && selectedPractitioner && clinic?.id && (
        <ResubmitPractitionerSheet
          open={resubmitSheetOpen}
          onOpenChange={setResubmitSheetOpen}
          clinicId={clinic?.id}
          practitioner={selectedPractitioner}
          onSuccess={() => {
            setResubmitSheetOpen(false);
            setSelectedPractitioner(null);
            fetchPractitioners();
          }}
        />
      )}
      {editPractitionerSheetOpen && selectedPractitioner && clinic?.id && (
        <EditPractitionerSheet
          open={editPractitionerSheetOpen}
          onOpenChange={setEditPractitionerSheetOpen}
          practitioner={selectedPractitioner}
          clinicId={clinic?.id}
          onPractitionerUpdated={() => {
            setEditPractitionerSheetOpen(false);
            setSelectedPractitioner(null);
            fetchPractitioners();
          }}
        />
      )}
    </div>
  );
};

export default PractitionersPage;
